import { reverse, sortBy, isNumber, orderBy, last } from 'lodash'
import moment from 'moment'

import { str2moment, getToday } from '../../../lib/Tools.js'
import { getStatusValue } from '../TripStatus.js'

import { 
  getLeaderName, countAdults, countKids, 
  calculateTripPrice, getPaidTotals
} from '../../../lib/TripTools.js'

/**
 * 
 */
export function arrangeTripsList(trips: Object[], col: string, direction: "ASC"|"DESC"|"NONE"): Object[] {
  let sorted = null
   
  if (col === "pendingTasks") sorted = sortBy(trips, ["pendingTasks", "arrivalV"])
  if (col === "creation")     sorted = sortBy(trips, ["creationV", "arrivalV"])
  if (col === "urgency")      sorted = sortBy(trips, ["remainingP", "finalPrice"])
  if (col === "bill")         sorted = orderBy(trips, ["receiptDate", "receiptNumber"], ["desc", "desc"])
  if (col === "price")        sorted = sortBy(trips, t => -1 * t.finalPrice)
  if (col === "paid")         sorted = sortBy(trips, ["paid", "finalPrice", "remainingP"])
  if (col === "status")       sorted = sortBy(trips, ["statusV", "remainingP", "finalPrice"])
  if (col === "class")        sorted = sortBy(trips, ["classV", "statusV", "paid", "finalPrice"])
  if (col === "stars")        sorted = sortBy(trips, ["starsV", "classV", "statusV", "paid", "finalPrice"])

  if (col === "arrival") {
    // #TODO time is an input to the component, part of the state; should come thru props
    const today = getToday()
    
    const missingDateTrips = trips.filter(t => !t.arrival || !t.return)
    const datedTrips = trips.filter(t => t.arrival && t.return)

    const soon   = sortBy(datedTrips.filter(t => today.isBefore(t.arrival, "days") 
                                               && moment.duration(t.arrival.diff(today)).asDays() <= 7), "arrivalV")
    const here   = sortBy(datedTrips.filter(t => today.isBetween(t.arrival, t.return, "day", "[]")), "arrivalV")
    const future = sortBy(datedTrips.filter(t => today.isBefore(t.arrival, "days")
                                               && moment.duration(t.arrival.diff(today)).asDays() > 7), "arrivalV")
    const past   = sortBy(datedTrips.filter(t => today.isAfter(t.return, "days")), "returnV")
 
    const list = [
      ...((missingDateTrips.length > 0) ? ["Clientes a los que les faltan fechas", ...missingDateTrips] : []),
      ...((soon.length > 0)   ? ["Clientes que llegan en la próxima semana", ...soon] : []),
      ...((here.length > 0)   ? ["Clientes visitando España ahora", ...here] : []),
      ...((future.length > 0) ? ["Clientes que llegan en el futuro", ...future] : []),
      ...((past.length > 0)   ? ["Clientes que ya han vuelto a china", ...past] : []),
    ]

    sorted = list
  }

  return direction == "ASC" ? sorted : reverse(sorted)
}

/**
 * 
 */

const thisYear = () => moment().years()
const format = (date => (date ? moment(date).format("MM/DD") : "??/??"))
const formatWithYear = (date => (date ? moment(date).format("YY/MM/DD") : "??/??"))

const formatStartDate = date => {
  if (!date) return "??/??"
  
  const m = moment(date)

  return (m.years() == thisYear()
    ? m.format("MM/DD")
    : m.format("YYYY/MM/DD")
  )
}

const datesDiff = (a, b) => (a && b ? `, ${moment(b).diff(moment(a), "days") + 1}d` : "")

export const processTripsList = (trips, Routes, Activities, Cities, getPendingTasksCount) => {
  return trips.map(t => {
    /* NOTA: sobre la urgencia y los tiempos
      * la urgencia es básicamente el tiempo que queda para la llegada como fracción del tiempo
      * disponible para hacer el viaje: (llegada - now)/(llegada - creacion)
      * Si la fracción es < 10%: verde. 20%: naranja. más tarde, rojo.
      * Con dos excepciones:
      * - el mínimo tiempo para hacer las reservas sin problemas ronda las 2 semanas.
      * - con más de 6 meses el cliente no va a tener prisa, tampoco puede ser urgente
      */
    const remainingTime = t.startDate ? str2moment(t.startDate).diff(getToday(), "days") : 365
    const totalTime = t.startDate ? str2moment(t.startDate).diff(moment(t.creation), "days") : null

    const _rt = isNumber(remainingTime) && (remainingTime - 14)
    const _tt = isNumber(totalTime) && Math.max(totalTime, 6 * 30)

    const remainingP = (
      (_rt <= 0 || _tt <= 0) // @TODO is this ok?
        ? 0 
        : Math.floor(Math.min(_rt / _tt, 1) * 100)
    )

    const price = calculateTripPrice(t, Routes[t.mainRoute], Activities, Cities)

    const adults = countAdults(t)
    const kids = countKids(t)

    return ({
      _id:        t._id,
      lastComment: t.notes.length > 0 ? last(t.notes).text : "", 
      creation:   formatWithYear(t.creation),
      creationV:  t.creation ? -parseInt(t.creation.format ? t.creation.format("YYMMDD") : moment(t.creation).format("YYMMDD")) : Infinity, 
      name:       t.name || "",
      realName:   t.realName || "",
      officialName: t.officialName || "",
      leaderName: getLeaderName(t) || "",
      wechat:     t.wechat || "",
      dates:      `${formatStartDate(t.startDate)} a ${format(t.endDate)}${datesDiff(t.startDate, t.endDate)}`,
      arrivalV:   t.startDate ? moment(t.startDate).format("YYMMDD") : "???", 
      arrival:    t.startDate ? moment(t.startDate) : null, 
      returnV:    t.endDate ? moment(t.endDate).format("YYMMDD") : null, 
      return:     t.endDate ? moment(t.endDate) : null, 
      status:     t.status,
      statusV:    -getStatusValue(t.status), 
      encargado:  t.encargado || "", 
      people:     `${adults}👤${kids ? ` + ${kids}👶` : ""}`,
      pendingTasks: getPendingTasksCount(t._id), 
      stars: t.stars, 
      starsV: isNumber(t.stars) ? t.stars : -1, 
      receiptNumber: t.receiptNumber,
      receiptDate: t.receiptDate,
      receiptYear: t.receiptYear,
      paid: getPaidTotals(t).eur, 

      remainingTime,
      totalTime,

      remainingP, 
      elapsedP: 100 - remainingP, 

      price, 
      finalPrice: price.finalE, 

      tags: t.tags
    })
  })
}
